import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import TopHeader from "@/components/TopHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, Users, Heart, MessageCircle, Send, 
  Sparkles, Baby, ThumbsUp, Reply, MoreHorizontal,
  Image as ImageIcon, EyeOff
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const CircleForum = () => {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [circle, setCircle] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [posting, setPosting] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [replyAnonymous, setReplyAnonymous] = useState(false);
  const postsEndRef = useRef(null);

  const pollingRef = useRef(null);

  useEffect(() => {
    fetchCircleData();
    
    // Poll for new posts every 15 seconds
    pollingRef.current = setInterval(async () => {
      try {
        const postsRes = await axios.get(`${API}/circles/${circleId}/posts`, { withCredentials: true });
        setPosts(postsRes.data);
      } catch (error) {
        // Silently fail on poll - don't show toast for background refresh
        console.error("Poll posts error:", error);
      }
    }, 15000);
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [circleId]);

  const fetchCircleData = async () => {
    setLoading(true);
    try {
      const [circleRes, postsRes] = await Promise.all([
        axios.get(`${API}/circles/${circleId}`, { withCredentials: true }),
        axios.get(`${API}/circles/${circleId}/posts`, { withCredentials: true })
      ]);
      setCircle(circleRes.data);
      setPosts(postsRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 403) {
        toast.error("You must join this circle first");
        navigate("/circles");
      } else {
        toast.error("Failed to load circle");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.trim()) return;
    
    setPosting(true);
    try {
      const response = await axios.post(`${API}/circles/${circleId}/posts`, {
        content: newPost,
        is_anonymous: isAnonymous
      }, { withCredentials: true });
      
      // Add the new post to the list immediately
      if (response.data) {
        setPosts(prevPosts => [response.data, ...prevPosts]);
      }
      
      setNewPost("");
      setIsAnonymous(false);
      toast.success("Post created!");
    } catch (error) {
      console.error("Create post error:", error);
      toast.error(error.response?.data?.detail || "Failed to create post");
      // Still try to refresh in case post was created
      fetchCircleData();
    } finally {
      setPosting(false);
    }
  };

  const handleReact = async (postId, emoji) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/react`, {
        emoji
      }, { withCredentials: true });
      
      // Update reactions in local state immediately
      setPosts(prevPosts => prevPosts.map(post => 
        post.post_id === postId 
          ? { ...post, reactions: response.data.reactions }
          : post
      ));
    } catch (error) {
      console.error("React error:", error);
      toast.error("Failed to react");
    }
  };

  const handleComment = async (postId) => {
    if (!replyContent.trim()) return;
    
    try {
      const response = await axios.post(`${API}/posts/${postId}/comments`, {
        content: replyContent,
        is_anonymous: replyAnonymous
      }, { withCredentials: true });
      
      // Update comments in local state immediately
      if (response.data) {
        setPosts(prevPosts => prevPosts.map(post => 
          post.post_id === postId 
            ? { ...post, comments: [...(post.comments || []), response.data] }
            : post
        ));
      }
      
      setReplyContent("");
      setReplyingTo(null);
      setReplyAnonymous(false);
      toast.success("Comment added!");
    } catch (error) {
      console.error("Comment error:", error);
      toast.error(error.response?.data?.detail || "Failed to add comment");
    }
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'ttc': return <Sparkles className="w-4 h-4" />;
      case 'pregnant': return <Heart className="w-4 h-4" />;
      case 'postpartum': return <Baby className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream pb-24">
        <Navigation />
        <div className="px-6 pt-6 max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-sage/10 rounded w-1/3" />
            <div className="h-40 bg-sage/10 rounded-xl" />
            <div className="h-32 bg-sage/10 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!circle) {
    return (
      <div className="min-h-screen bg-cream pb-24">
        <Navigation />
        <div className="px-6 pt-6 text-center">
          <p className="text-muted-foreground">Circle not found</p>
          <Link to="/circles">
            <Button className="mt-4">Back to Circles</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      <TopHeader title={circle?.name || "Circle"} />
      
      {/* Circle Info Header */}
      <div className="px-6 py-4 border-b border-sage/10 bg-white/50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl overflow-hidden">
              <img 
                src={circle.image_url || "https://images.unsplash.com/photo-1581900016271-bab836b53546?crop=entropy&cs=srgb&fm=jpg&q=85&w=200"}
                alt={circle.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Badge className={`phase-${circle.phase}`}>
                  {getPhaseIcon(circle.phase)}
                  <span className="ml-1 capitalize">{circle.phase}</span>
                </Badge>
              </div>
              <p className="text-muted-foreground text-sm mt-1">{circle.member_count} members</p>
            </div>
          </div>
        </div>
      </div>

      <main className="px-6 pt-6 max-w-4xl mx-auto space-y-6">
        {/* New Post */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <Avatar className="w-10 h-10">
                {isAnonymous ? (
                  <AvatarFallback className="bg-gray-200 text-gray-500">
                    <EyeOff className="w-5 h-5" />
                  </AvatarFallback>
                ) : (
                  <>
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-sage/20 text-sage-dark">
                      {user?.name?.[0]}
                    </AvatarFallback>
                  </>
                )}
              </Avatar>
              <div className="flex-1">
                <Textarea
                  placeholder={isAnonymous ? "Share anonymously with the group..." : "Share something with the group..."}
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  className="bg-sage/5 border-sage/20 resize-none min-h-[80px]"
                  data-testid="circle-post-input"
                />
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="anonymous-post"
                      checked={isAnonymous}
                      onCheckedChange={setIsAnonymous}
                      className="data-[state=checked]:bg-sage"
                    />
                    <Label htmlFor="anonymous-post" className="text-sm text-muted-foreground flex items-center gap-1 cursor-pointer">
                      <EyeOff className="w-4 h-4" />
                      Post anonymously
                    </Label>
                  </div>
                  <Button 
                    onClick={handleCreatePost}
                    disabled={!newPost.trim() || posting}
                    className="bg-sage text-white hover:bg-sage-dark rounded-full"
                    data-testid="circle-post-submit"
                  >
                    {posting ? "Posting..." : "Post"}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Posts */}
        {posts.length === 0 ? (
          <div className="text-center py-16">
            <MessageCircle className="w-16 h-16 text-sage/40 mx-auto mb-4" />
            <h3 className="text-xl font-serif text-sage-darker mb-2">No posts yet</h3>
            <p className="text-muted-foreground">Be the first to start a conversation!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <Card key={post.post_id} className="bg-white border-sage/20">
                <CardContent className="p-4">
                  {/* Post Header */}
                  <div className="flex items-start gap-3">
                    <Avatar className="w-10 h-10">
                      {post.is_anonymous ? (
                        <AvatarFallback className="bg-gray-200 text-gray-500">
                          <EyeOff className="w-5 h-5" />
                        </AvatarFallback>
                      ) : (
                        <>
                          <AvatarImage src={post.user?.picture} />
                          <AvatarFallback className="bg-sage/20 text-sage-dark">
                            {post.user?.name?.[0]}
                          </AvatarFallback>
                        </>
                      )}
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sage-darker">
                          {post.is_anonymous ? "Anonymous" : post.user?.name}
                        </p>
                        {post.is_anonymous && (
                          <Badge variant="outline" className="text-xs border-gray-300 text-gray-500">
                            <EyeOff className="w-3 h-3 mr-1" />
                            Anonymous
                          </Badge>
                        )}
                        <span className="text-muted-foreground text-xs">
                          {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      
                      {/* Post Content */}
                      <p className="text-sage-darker mt-2 whitespace-pre-wrap">{post.content}</p>
                      
                      {post.image_url && (
                        <img 
                          src={post.image_url} 
                          alt="Post attachment"
                          className="mt-3 rounded-xl max-h-80 object-cover"
                        />
                      )}
                      
                      {/* Reactions */}
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-sage/10">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReact(post.post_id, '❤️')}
                          className={`text-muted-foreground hover:text-red-500 ${
                            post.reactions?.['❤️']?.includes(user?.user_id) ? 'text-red-500' : ''
                          }`}
                        >
                          ❤️ {post.reactions?.['❤️']?.length || 0}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReact(post.post_id, '👏')}
                          className="text-muted-foreground hover:text-yellow-500"
                        >
                          👏 {post.reactions?.['👏']?.length || 0}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReact(post.post_id, '🤗')}
                          className="text-muted-foreground hover:text-orange-500"
                        >
                          🤗 {post.reactions?.['🤗']?.length || 0}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setReplyingTo(replyingTo === post.post_id ? null : post.post_id)}
                          className="text-muted-foreground hover:text-sage-dark ml-auto"
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          {post.comments?.length || 0}
                        </Button>
                      </div>
                      
                      {/* Comments */}
                      {post.comments && post.comments.length > 0 && (
                        <div className="mt-3 space-y-3">
                          {post.comments.map((comment) => (
                            <div key={comment.comment_id} className="flex gap-2 pl-4 border-l-2 border-sage/20">
                              <Avatar className="w-8 h-8">
                                {comment.is_anonymous ? (
                                  <AvatarFallback className="bg-gray-200 text-gray-500">
                                    <EyeOff className="w-4 h-4" />
                                  </AvatarFallback>
                                ) : (
                                  <>
                                    <AvatarImage src={comment.user?.picture} />
                                    <AvatarFallback className="bg-sage/10 text-sage-dark text-xs">
                                      {comment.user?.name?.[0]}
                                    </AvatarFallback>
                                  </>
                                )}
                              </Avatar>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-sage-darker">
                                    {comment.is_anonymous ? "Anonymous" : comment.user?.name}
                                  </p>
                                  <span className="text-muted-foreground text-xs">
                                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                                  </span>
                                </div>
                                <p className="text-sm text-sage-darker mt-1">{comment.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Reply Input */}
                      {replyingTo === post.post_id && (
                        <div className="mt-3 space-y-2">
                          <div className="flex gap-2">
                            <Avatar className="w-8 h-8">
                              {replyAnonymous ? (
                                <AvatarFallback className="bg-gray-200 text-gray-500">
                                  <EyeOff className="w-4 h-4" />
                                </AvatarFallback>
                              ) : (
                                <>
                                  <AvatarImage src={user?.picture} />
                                  <AvatarFallback className="bg-sage/10 text-sage-dark text-xs">
                                    {user?.name?.[0]}
                                  </AvatarFallback>
                                </>
                              )}
                            </Avatar>
                            <div className="flex-1 flex gap-2">
                              <Textarea
                                placeholder={replyAnonymous ? "Write anonymously..." : "Write a comment..."}
                                value={replyContent}
                                onChange={(e) => setReplyContent(e.target.value)}
                                className="bg-sage/5 border-sage/20 resize-none min-h-[60px] text-sm"
                              />
                              <Button
                                size="sm"
                                onClick={() => handleComment(post.post_id)}
                                disabled={!replyContent.trim()}
                                className="bg-sage text-white hover:bg-sage-dark"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pl-10">
                            <Switch
                              id={`anonymous-reply-${post.post_id}`}
                              checked={replyAnonymous}
                              onCheckedChange={setReplyAnonymous}
                              className="data-[state=checked]:bg-sage scale-75"
                            />
                            <Label htmlFor={`anonymous-reply-${post.post_id}`} className="text-xs text-muted-foreground flex items-center gap-1 cursor-pointer">
                              <EyeOff className="w-3 h-3" />
                              Reply anonymously
                            </Label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        <div ref={postsEndRef} />
      </main>
    </div>
  );
};

export default CircleForum;
